import React, { Component } from 'react';
import { styled } from '@compiled/react';
import isNil from 'lodash/isNil';
import { DatePicker } from '@atlaskit/datetime-picker';
import { colors } from '@atlaskit/theme';
import { token } from '@atlaskit/tokens';
import Select from '@atlassian/jira-common-analytics-v2-wrapped-components/src/select';
import { gridSize } from '@atlassian/jira-common-styles/src/main.tsx';
import { injectIntlV2 as injectIntl } from '@atlassian/jira-intl/src/v2/inject.tsx';
import { fireUIAnalytics } from '@atlassian/jira-product-analytics-bridge';
import { QUICK_DATE_FILTER_OPTIONS } from './constants';
import messages from './messages';
import type { Props, QUICK_DATE_OPTION } from './types';
import { getControlsStateFromZoom, parseDatePickerDate } from './utils';

// eslint-disable-next-line jira/react/no-class-components
class Controls extends Component<Props> {
	// eslint-disable-next-line @typescript-eslint/no-explicit-any
	onChangeFrom = (value: string, analyticsEvent: any) => {
		const { quickDateFilter, zoomState } = this.props;
		const derivedState = getControlsStateFromZoom(zoomState, quickDateFilter);
		const toDate = new Date(derivedState.toDate);
		let date = value ? parseDatePickerDate(value) : null;

		if (!isNil(date)) {
			if (date.getTime() > toDate.getTime()) {
				date = toDate;
			} else if (date.getTime() > new Date().getTime()) {
				date = new Date();
			}
		}
		this.props.setZoomStartDate(date);

		fireUIAnalytics(analyticsEvent, 'dateFilter', {
			from: derivedState.fromDate,
			to: value,
			label: 'From date',
			currentState: derivedState,
		});
	};

	// eslint-disable-next-line @typescript-eslint/no-explicit-any
	onChangeTo = (value: string, analyticsEvent: any) => {
		const { quickDateFilter, zoomState } = this.props;
		const derivedState = getControlsStateFromZoom(zoomState, quickDateFilter);
		const fromDate = new Date(derivedState.fromDate);
		let date = value ? parseDatePickerDate(value) : null;

		if (!isNil(date)) {
			if (date.getTime() < fromDate.getTime()) {
				date = fromDate;
			} else if (date.getTime() > new Date().getTime()) {
				date = new Date();
			}
		}
		this.props.setZoomEndDate(date);

		fireUIAnalytics(analyticsEvent, 'dateFilter', {
			from: derivedState.toDate,
			to: value,
			label: 'To date',
			currentState: derivedState,
		});
	};

	// eslint-disable-next-line @typescript-eslint/no-explicit-any
	onChangeQuickDateFilter = (option: QUICK_DATE_OPTION, action: any, analyticsEvent: any) => {
		const { quickDateFilter, zoomState } = this.props;
		const derivedState = getControlsStateFromZoom(zoomState, quickDateFilter);
		fireUIAnalytics(analyticsEvent, 'dateFilter', {
			from: derivedState.selectedOption,
			to: option,
			label: 'Date filter',
			currentState: derivedState,
		});
		this.props.setQuickDateFilter(option.value);
	};

	render() {
		const { intl, isDisabled, lowerOpacity, zoomState, quickDateFilter } = this.props;
		const derivedState = getControlsStateFromZoom(zoomState, quickDateFilter);

		return (
			<ControlsContainer lowerOpacity={lowerOpacity}>
				<ControlContainer>
					<ControlLabel htmlFor="cfd-quick-date-filter">
						{intl.formatMessage(messages.dateFilterLabel)}
					</ControlLabel>
					<Select
						isDisabled={isDisabled}
						id="cfd-quick-date-filter"
						aria-label={intl.formatMessage(messages.dateFilterLabel)}
						value={derivedState.selectedOption}
						onChange={this.onChangeQuickDateFilter}
						options={QUICK_DATE_FILTER_OPTIONS}
					/>
				</ControlContainer>
				<ControlContainer>
					<ControlLabel htmlFor="cfd-from-date">
						{intl.formatMessage(messages.fromDateLabel)}
					</ControlLabel>
					<DatePicker
						id="cfd-from-date"
						isDisabled={isDisabled}
						// @ts-expect-error - TS2322 - Type '(value: string, analyticsEvent: any) => void' is not assignable to type '((value: string) => void) & ((value: string) => void)'.
						onChange={this.onChangeFrom}
					/>
				</ControlContainer>
				<ControlContainer>
					<ControlLabel htmlFor="cfd-to-date">
						{intl.formatMessage(messages.toDateLabel)}
					</ControlLabel>
					<DatePicker
						id="cfd-to-date"
						isDisabled={isDisabled}
						// @ts-expect-error - TS2322 - Type '(value: string, analyticsEvent: any) => void' is not assignable to type '((value: string) => void) & ((value: string) => void)'.
						onChange={this.onChangeTo}
					/>
				</ControlContainer>
			</ControlsContainer>
		);
	}
}

const ControlsWithIntl = injectIntl(Controls);
export default ControlsWithIntl;

// eslint-disable-next-line @typescript-eslint/no-explicit-any, @atlaskit/ui-styling-standard/no-styled -- To migrate as part of go/ui-styling-standard
const ControlsContainer = styled.div<{ lowerOpacity?: any }>({
	display: 'flex',
	// eslint-disable-next-line @typescript-eslint/no-explicit-any, @atlaskit/ui-styling-standard/no-dynamic-styles -- Ignored via go/DSP-18766
	opacity: (props: any) => (props.lowerOpacity ? 0.4 : 1),
	transition: 'opacity 0.4s ease-out',
});

// eslint-disable-next-line @atlaskit/ui-styling-standard/no-styled -- To migrate as part of go/ui-styling-standard
const ControlContainer = styled.div({
	marginRight: token('space.200', '16px'),
	// eslint-disable-next-line @atlaskit/ui-styling-standard/no-imported-style-values, @atlaskit/ui-styling-standard/no-unsafe-values -- Ignored via go/DSP-18766
	maxWidth: `${gridSize * 20}px`,
	flex: 1,
});

// eslint-disable-next-line @atlaskit/ui-styling-standard/no-styled -- To migrate as part of go/ui-styling-standard
const ControlLabel = styled.label({
	display: 'block',
	padding: `0 0 ${token('space.050', '4px')}`,
	// eslint-disable-next-line @atlaskit/ui-styling-standard/no-imported-style-values -- Ignored via go/DSP-18766
	color: token('color.text.subtlest', colors.N400),
	font: token('font.body.small'),
	fontWeight: 600,
	lineHeight: '16px',
	marginTop: token('space.200', '16px'),
});
